import { ReactComponent as IconPrevious } from '@/assets/svgs/previous-arrow.svg'
import AdditionalZone from '@/components/AdditionalZone'
import Breadcrumbs from '@/components/Breadcrumbs'
import Button from '@/components/Button'
import Portal from '@/components/Portal'
import SelectorBox2 from '@/components/SelectorBox2'
import useComputed from '@/hooks/use-computed'
import useConfig from '@/hooks/use-config'
import useMessages from '@/hooks/use-messages'
import usePage from '@/hooks/use-page'
import "@/translations/i18n"
import handle from '@/utils/handle'
import rem from '@/utils/rem'
import classname from 'classnames'
import produce from 'immer'
import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import KeyboardEventHandler from '@infinium/react-keyboard-event-handler'
import { useNavigate, useLocation } from '@/lib/router-dom'
import api from '@/services/api/simulateData.js'
import Toast2 from '@/components/Toast2'
import useModal from '@/hooks/use-modal/index';
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg'
import ReactDOM from 'react-dom';
import useBehalfPanel from '@/hooks/use-behalf-panel'
import Stepper from '@/componentsDS/Stepper/Stepper'
import { ReactComponent as IconConfigure } from '@/assets/svgs/icon-configure.svg';


const SimulateReceive = () => {

	/*=================================================
		General
	/================================================*/

	const { t } = useTranslation();
	const [pageTitleObj] = useState(t("requestData"))

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({ title: pageTitleObj, breadcrumbs: [t("cdiPitstop"), t("requestData"), pageTitleObj, t("chooseDataElement")] })
	const { goTo } = useNavigate();

	/*=================================================
		Modal
	/================================================*/
	const { visible: isVisible, show: showSimulateReceiveCancelModal, hide: hideSimulateReceiveCancelModal } = useModal()
	const { visible: isSubmitModalVisible, show: showSimulateReceiveSubmitModal, hide: hideSimulateReceiveSubmitModal } = useModal()

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()
	function handleError(){
		setError()
	}

	/*=================================================
		API
	/================================================*/

	const { identity, consumeItems, error: errorConfig } = useConfig()
	const { error: errorMessages } = useMessages()
	const [selectedDataElement, setSelectedDataElement, [participants]] = useComputed({},
		value => value?.from?.map(element => element).reduce((arr, curr) => ([...arr, curr]), [{ id: "MY_PITSTOP", name: t("myPitstop"), iconUrl: identity.organization.icon_url, on_behalf_of: [] }])
	)

	const [selectedParticipants, setSelectedParticipants] = useState([])
	const [selectedOnBehalf, setSelectedOnBehalf] = useState(null)
	const [onBehalfOf, setOnBehalfOf] = useState([])
	const [, setReferenceId] = useState('')

	useEffect(() => {
		if (selectedParticipants.find(item => item.id === 'MY_PITSTOP')) {
			setOnBehalfOf(participants.reduce((pre, cur) => [...pre, ...cur.on_behalf_of], []).filter((val, idx, arr) => idx === arr.findIndex(v => v.id === val.id)))
		} else {
			setOnBehalfOf(selectedParticipants.reduce((pre, cur) => [...pre, ...cur.on_behalf_of], []))
		}
	}, [selectedParticipants])

	useEffect(() => {
		setSelectedParticipants([])
	}, [selectedDataElement])

	const [ behalfPanel ] = useBehalfPanel(onBehalfOf)

	const ElementsList = () => {

		if (!consumeItems) {
			return (
				<div></div>
			)
		}

		const sortedConsumeItems = orderBy(consumeItems, [item => item.name.toLowerCase()], ['asc'])

		const list = sortedConsumeItems.map((element, index) => {

			const active = selectedDataElement.id === element.id

			return (
				<div className="data-element-selectors__col" key={index} onClick={() => setSelectedDataElement(element)}>
					<SelectorBox2 label={element.name} active={active} />
				</div>
			)
		})

		return list.length > 0 ? list : <div className="data-element-selectors__col">There are no data elements to simulate receive</div>
	}

	const ParticipantsList = () => {

		if (!participants) {
			return ''
		}

		const list = participants.filter(participant=> participant.id === "538a889b-df92-4e40-b796-bca7510501a5").map((element, index) => {

			const active = selectedParticipants.find(participant => participant.id === element.id)
			const activeIndex = selectedParticipants.findIndex(participant => participant.id === element.id)

			function toggleParticipant() {
				if (active) {
					const updatedSelectedParticipantsE = produce(selectedParticipants, draft => {
						draft.splice(activeIndex, 1)
					})
					return setSelectedParticipants(updatedSelectedParticipantsE)
				}

				const updatedSelectedParticipantsSimulateReceive = produce(selectedParticipants, draft => {
					draft.splice(activeIndex, 1)
					draft.push(element)
				})
				return setSelectedParticipants(updatedSelectedParticipantsSimulateReceive)
			}

			return (
				<div className="data-element-selectors__col simulate_receive" key={index} onClick={() => toggleParticipant()}>
					<SelectorBox2 label={element.name} image={element.iconUrl} active={active} />
				</div>
			)
		})

		return list.length > 0 ? list : <div className="data-element-selectors__col simulate_receive">There are no participants</div>

	}

	useEffect(()=>{
		setError(errorConfig)
		setError(errorMessages)
	}, [errorConfig, errorMessages])

	/*=================================================
		Steps
	/================================================*/
	const [steps] = useState([
		{stepTitle: t("chooseDataElement")},
		{stepTitle: t("chooseParticipant")},
		{stepTitle: t("additionalInformation")},
])
	const [step, setStep] = useState(1)

	const [stepOneStatus, setStepOneStatusFn] = useState(false)
	const [stepTwoStatus, setStepTwoStatusFn] = useState(false)
	const [stepThreeBStatus, setStepThreeBStatus] = useState(true)

	useEffect(() => {

		if (selectedDataElement && selectedDataElement.id) {
			return setStepOneStatusFn(true)
		}

		setStepOneStatusFn(false)
	}, [selectedDataElement])

	useEffect(() => {

		if (selectedParticipants.length > 0) {
			return setStepTwoStatusFn(true)
		}

		setStepTwoStatusFn(false)
	}, [selectedParticipants])

	useEffect(() => {

		resetErrors()

		const updatedBreadcrumbs = produce(breadcrumbsItems, draft => {
			draft[draft.length - 1] = steps[step - 1].stepTitle
		})
		return setBreadcrumbsItems(updatedBreadcrumbs)

	}, [step])


	function goToStepFn(stepObj) {
		if (stepObj < 1 || stepObj > stepObj.length) {
			return
		}
		setStep(stepObj)
	}

	function prevStepFn() {
		goToStepFn(step - 1)
	}

	function nextStep() {
		goToStepFn(step + 1)
	}

	/*=================================================
		Submit
	/================================================*/
	const [submitLoading, setSubmitLoading] = useState()

	async function submit() {

		if (submitLoading) {
			return
		}

		setSubmitLoading(true)

		const payload = {
			participants: [{
				id: selectedParticipants[0].id
			}],
			on_behalf_of: selectedOnBehalf ? [selectedOnBehalf] : []
		}

		const [errorV] = await api.simulate(selectedDataElement.id, payload, 'receive')

		setSubmitLoading(false)

		if(errorV){
			return setError(errorV?.error?.message || "Unable to simulate receive.")
		}

		goTo('/received-data/request/success')
	}

	function resetErrors() {
		setError()
	}

	/*=================================================
		Routes
	/================================================*/

	function goBack() {
		goTo('/shared-data')
	}

	function onSelectedOnBehalf(params) {
		if (behalfPanel && params && params.id && params.id !== 'no') {
			setSelectedOnBehalf(params)
			setStepThreeBStatus(true)
		} else if (!behalfPanel) {
			setStepThreeBStatus(true)
		} else {
			setStepThreeBStatus(false)
		}
	}

	function onInputCallback(params) {
		if (params) {
			setReferenceId(params)
		}
	}

	function getNextOrSubmit(type){
		if (type==="text"){
			if(onBehalfOf.length>0){
				return t("next")
			}
			return t("submit")
		}
		if(type==="function"){
			if(onBehalfOf.length>0){
				return nextStep()
			}
			return showSimulateReceiveSubmitModal()
		}
	}

	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitleObj}
					</div>
				</div>

				<div className="title-header__right">

				</div>
			</div>

			<Stepper steps={steps} currentStep={step-1}/>

			<hr />

			{step === 1 && (
				<>
					<span className="t--share-data-subheader">{t("chooseDataElementToShare")}</span>
					<div className="data-element-selectors" >
						<div className="data-element-selectors__cols">
							<ElementsList />
						</div>
					</div>
				</>
			)}

			{step === 2 && (
				<>
					<span className="t--share-data-subheader">{t("chooseWhoAccessData")}</span>
					<div className="data-element-selectors" >
						<div className="data-element-selectors__cols">
							<ParticipantsList />
						</div>
					</div>
				</>
			)}

			{step === 3 && (
				<>
					<div className="layout-upload-data">
						<div className="layout-upload-data__left">
							<div className="additional-zone__box">
								<AdditionalZone
									on_behalf_of={onBehalfOf}
									callback={params => onSelectedOnBehalf(params)}
									callbackInput={params => onInputCallback(params)}
								/>
							</div>
						</div>
					</div>
				</>
			)}


			<div style={{ height: rem(72) }}></div>
			<div className="DS__layout__footer">
				<div className="layout-default__sticky-actions">
					<KeyboardEventHandler handleKeys={["alt+right", "alt+left", "enter", "esc"]} onKeyEvent={(key, _e) => {
						if (key === "alt+left" || "esc") {
							prevStepFn()
						}
						if (key === "alt+right" ||  "enter") {
							if (stepOneStatus && step === 1) {
								nextStep()
							}
							if (stepTwoStatus && step === 2) {
								nextStep()
							}
							if (stepThreeBStatus && step === steps.length) {
								showSimulateReceiveSubmitModal()
							}
						}
					}} />
					<div className="layout-default__sticky-actions__left">
						{step > 1 &&
							<Button text={t("previous")}
								sizing='Medium' ghost full round maxWidth='154'
								submit={() => prevStepFn()}
							/>
						}
					</div>
					<div className="layout-default__sticky-actions__right">
						<div className="overview__button_wrap">
							<Button text={t("Cancel")}
								sizing='Cancel' full noborder maxWidth='154'
								submit={() => showSimulateReceiveCancelModal()}
							/>

							{step === 1 &&
								<Button text={t("next")}
									sizing='Medium' full maxWidth='154'
									disabled={!stepOneStatus}
									submit={() => nextStep()}
								/>
							}
							{step === 2 &&
								<Button text={getNextOrSubmit("text")}
									sizing='Medium' full  maxWidth='154'
									disabled={!stepTwoStatus}
									submit={() => {
										getNextOrSubmit("function")
									}}
								/>
							}
							{step === 3 &&
								<Button
									text={t("submit")}
									id="simulate-receive-submit-button-id"
									sizing='Medium' full round maxWidth='154'
									disabled={!stepThreeBStatus}
									submit={() => showSimulateReceiveSubmitModal()}
								/>
							}

						</div>
					</div>
				</div>
			</div>

			<Modal visible={isVisible} variant='medium' close={hideSimulateReceiveCancelModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<IconConfigure />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Cancel Simulation
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(14), textAlign: 'center' }}>Are you sure you want to cancel Simulation?</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
						<Button ghost text="Cancel"
								submit={() => {
									hideSimulateReceiveCancelModal()
								}} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
								<Button text="Confirm"
								submit={() => {
									hideSimulateReceiveCancelModal();
									goBack()
								}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={isSubmitModalVisible} variant='medium' close={hideSimulateReceiveSubmitModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<IconConfigure />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Submit Simulation
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(14), textAlign: 'center' }}>Are you sure you want to save Simulation?</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
						<Button ghost text="Cancel"
								submit={() => {
									hideSimulateReceiveSubmitModal()
								}} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
								<Button text="Confirm"
								submit={() => {
									submit();
									hideSimulateReceiveSubmitModal()
								}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			{
				submitLoading && (
					ReactDOM.createPortal(
						<div className='modal white'>
							<div className='loading'>
								<img src="/loading.gif"/>
							</div>
						</div>
						, document.body
					)
				)
			}

			<Toast2 show={error} errorMessage={error} close={()=>{handleError()}} />
		</>
	)
}

export default SimulateReceive
